import React, { useState, useEffect, useRef } from 'react';
import { format, addDays, startOfWeek, isSameDay, isBefore, startOfDay, getDay } from 'date-fns';
import SEO from '../../components/common/SEO';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useServices } from '../../hooks/useServices';
import { bookingService } from '../../services/firebase/bookingService';
import { availabilityService } from '../../services/firebase/availabilityService';
import { eventService } from '../../services/firebase/eventService';
import { invoiceService } from '../../services/firebase/invoiceService';
import { emailService } from '../../services/email/emailService';
import './Booking.css';

// Smooth scroll helper with offset for fixed header
const scrollToElement = (element) => {
  if (element) {
    setTimeout(() => {
      const headerOffset = 100; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }, 150);
  }
};

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Default capacity per time slot (can be overridden by admin)
const DEFAULT_SLOT_CAPACITY = 1;

const Booking = () => {
  const { services, loading: servicesLoading } = useServices();
  const [bookingType, setBookingType] = useState('service'); // 'service' or 'event'
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [bufferBlockedSlots, setBufferBlockedSlots] = useState([]);
  const [eventBlockedSlots, setEventBlockedSlots] = useState([]);
  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [slotBookingCounts, setSlotBookingCounts] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [workingHours, setWorkingHours] = useState(null);
  const [enabledDays, setEnabledDays] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [bookingSettings, setBookingSettings] = useState(null);
  const [requestCustomTime, setRequestCustomTime] = useState(false);
  const [customTimeValue, setCustomTimeValue] = useState('');

  // Events state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Refs for auto-scroll
  const dateStepRef = useRef(null);
  const timeStepRef = useRef(null);
  const detailsStepRef = useRef(null);
  const eventDetailsRef = useRef(null);

  // Load upcoming events and working schedule
  useEffect(() => {
    loadEvents();
    loadEnabledDays();
  }, []);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const upcomingEvents = await eventService.getUpcoming(20);
      // Filter to only show events with available spots
      setEvents(upcomingEvents.filter(e => e.status !== 'closed' && e.spots?.available > 0));
    } catch (err) {
      console.error('Error loading events:', err);
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const loadEnabledDays = async () => {
    try {
      const days = await availabilityService.getEnabledDays();
      setEnabledDays(days);
    } catch (err) {
      console.error('Error loading enabled days:', err);
      // Default to weekdays if error
      setEnabledDays({
        sunday: false,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false
      });
    }
  };

  // Generate time slots based on working hours
  const generateTimeSlots = (start, end) => {
    const slots = [];
    if (!start || !end) return slots;

    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Use < (not <=) so the end time itself is NOT a bookable slot start
    // e.g. if work ends at 16:00, last 1hr slot starts at 15:00
    for (let m = startMinutes; m < endMinutes; m += 60) {
      const hour = Math.floor(m / 60);
      const minute = m % 60;
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }

    return slots;
  };

  // Generate week dates (filter based on enabled days from admin)
  const weekDates = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i))
    .filter(date => {
      if (!enabledDays) {
        // Default: exclude weekends until we have enabled days
        const day = getDay(date);
        return day !== 0 && day !== 6;
      }
      const dayIndex = getDay(date);
      const dayName = DAY_NAMES[dayIndex];
      return enabledDays[dayName];
    });

  // Load availability when date changes
  useEffect(() => {
    if (selectedDate) {
      loadAvailability(selectedDate);
    }
  }, [selectedDate]);

  const loadAvailability = async (date) => {
    setLoadingAvailability(true);
    try {
      const {
        blocked,
        booked,
        bookingCounts,
        bufferBlocked,
        eventBlocked,
        events: dayEvents,
        workingHours: dayWorkingHours,
        isDayEnabled,
        bookingSettings: settings
      } = await availabilityService.getAvailabilityForDate(date);

      if (!isDayEnabled) {
        // Day is disabled
        setBlockedSlots([]);
        setBookedSlots([]);
        setBufferBlockedSlots([]);
        setSlotBookingCounts({});
        setEventBlockedSlots([]);
        setScheduledEvents([]);
        setAvailableSlots([]);
        setTimeSlots([]);
        setWorkingHours(null);
        setBookingSettings(null);
        return;
      }

      setBlockedSlots(blocked || []);
      setBookedSlots(booked || []);
      setBufferBlockedSlots(bufferBlocked || []);
      setSlotBookingCounts(bookingCounts || {});
      setEventBlockedSlots(eventBlocked || []);
      setScheduledEvents(dayEvents || []);
      setWorkingHours(dayWorkingHours);
      setBookingSettings(settings || null);

      // Generate time slots based on working hours
      const slots = dayWorkingHours?.enabled
        ? generateTimeSlots(dayWorkingHours.start, dayWorkingHours.end)
        : []; // No slots if working hours unavailable (avoids showing wrong default times)

      setTimeSlots(slots);

      // Calculate available slots based on capacity and buffer
      const available = slots.filter(slot => {
        const isBlocked = blocked && blocked.length > 0 && blocked.includes(slot);
        const isBufferBlocked = bufferBlocked && bufferBlocked.length > 0 && bufferBlocked.includes(slot);
        const currentBookings = bookingCounts?.[slot] || 0;
        const capacity = DEFAULT_SLOT_CAPACITY;
        return !isBlocked && !isBufferBlocked && currentBookings < capacity;
      });
      setAvailableSlots(available);
    } catch (err) {
      console.error('Error loading availability:', err);
      // On error, clear slots and show error - don't show hardcoded times that may be wrong
      setTimeSlots([]);
      setAvailableSlots([]);
      setBlockedSlots([]);
      setBookedSlots([]);
      setBufferBlockedSlots([]);
      setEventBlockedSlots([]);
      setScheduledEvents([]);
      setSlotBookingCounts({});
      setWorkingHours(null);
      setBookingSettings(null);
      setError('Unable to load available times. Please refresh the page or contact us directly to book.');
    } finally {
      setLoadingAvailability(false);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedEvent(null);
    setBookingType('service');
    setError(null);
    // Auto-scroll to date selection
    scrollToElement(dateStepRef.current);
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setBookingType('event');
    setError(null);
    // Auto-scroll to event details form
    scrollToElement(eventDetailsRef.current);
  };

  const handleDateSelect = (date) => {
    // Don't allow booking in the past
    if (isBefore(startOfDay(date), startOfDay(new Date()))) {
      return;
    }

    // Check if day is enabled
    const dayIndex = getDay(date);
    const dayName = DAY_NAMES[dayIndex];

    if (enabledDays && !enabledDays[dayName]) {
      setError('Bookings are not available on this day. Please select another date.');
      return;
    }

    setSelectedDate(date);
    setSelectedTime(null);
    setError(null);
    // Auto-scroll to time selection
    scrollToElement(timeStepRef.current);
  };

  // Check if a date is bookable (enabled in admin schedule)
  const isDateBookable = (date) => {
    if (!enabledDays) return true; // Default allow while loading

    const dayIndex = getDay(date);
    const dayName = DAY_NAMES[dayIndex];
    return enabledDays[dayName];
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setError(null);
    // Auto-scroll to details form
    scrollToElement(detailsStepRef.current);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (bookingType === 'service') {
      // For services that don't require consultation, need date and time (or custom time)
      if (!selectedService) {
        setError('Please select a service for your booking.');
        return;
      }
      if (!requiresConsultation(selectedService)) {
        if (!selectedDate) {
          setError('Please select a date for your booking.');
          return;
        }
        if (!selectedTime && !(requestCustomTime && customTimeValue)) {
          setError('Please select a time or request a custom time for your booking.');
          return;
        }
      }
    } else {
      if (!selectedEvent) {
        setError('Please select an event to register for.');
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (bookingType === 'event') {
        // Register for event
        await eventService.registerAttendee(selectedEvent.id, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          notes: formData.message
        });

        // Send event registration confirmation emails
        try {
          await emailService.init();

          const attendee = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            notes: formData.message
          };

          // Send confirmation to the customer
          const customerResult = await emailService.sendEventRegistrationConfirmation(selectedEvent, attendee);
          if (!customerResult.success) {
            console.warn('Event registration customer email failed:', customerResult.error);
          }

          // Send notification to admin (Kirstie)
          const adminResult = await emailService.sendEventRegistrationNotificationToAdmin(selectedEvent, attendee);
          if (!adminResult.success) {
            console.warn('Event registration admin email failed:', adminResult.error);
          }
        } catch (emailErr) {
          console.error('Error sending event registration emails:', emailErr);
        }
      } else {
        // Create service booking
        const bookingTime = requestCustomTime ? customTimeValue : selectedTime;
        const bookingNotes = requestCustomTime
          ? `[CUSTOM TIME REQUEST] ${formData.message || ''}\n\nRequested time: ${customTimeValue}`
          : formData.message;

        // For consultation services, date/time may be null
        const isConsultation = requiresConsultation(selectedService);

        const bookingResult = await bookingService.createBooking({
          ...formData,
          message: bookingNotes,
          service: selectedService.name,
          serviceId: selectedService.id,
          date: isConsultation ? null : format(selectedDate, 'yyyy-MM-dd'),
          time: isConsultation ? null : bookingTime,
          duration: selectedService.duration,
          price: selectedService.price,
          isCustomTimeRequest: requestCustomTime,
          isConsultationRequest: isConsultation
        });

        // Create invoice and send confirmation emails
        // Awaited to ensure emails are sent before user navigates away
        let invoice = null;
        try {
          // Create invoice if service has a numeric price (not for consultation requests)
          const servicePrice = parseFloat(selectedService.price);
          const isConsultationReq = requiresConsultation(selectedService);
          if (!isNaN(servicePrice) && servicePrice > 0 && !isConsultationReq) {
            try {
              const dateTimeStr = selectedDate && bookingTime
                ? ` - ${format(selectedDate, 'MMMM d, yyyy')} at ${bookingTime}`
                : '';
              invoice = await invoiceService.create({
                clientId: bookingResult.client.id,
                clientName: formData.name,
                clientEmail: formData.email,
                clientPhone: formData.phone,
                lineItems: [{
                  type: 'service',
                  referenceId: selectedService.id,
                  description: `${selectedService.name}${dateTimeStr}`,
                  quantity: 1,
                  unitPrice: servicePrice,
                  total: servicePrice,
                  taxable: true
                }],
                sourceType: 'booking',
                sourceId: bookingResult.booking.id,
                status: 'draft',
                publicNotes: `Thank you for booking ${selectedService.name}!`
              });

              // Link invoice to booking so admin can send it on confirmation
              if (invoice && invoice.id) {
                try {
                  await bookingService.update(bookingResult.booking.id, { invoiceId: invoice.id });
                } catch (linkErr) {
                  console.error('Failed to link invoice to booking:', linkErr);
                }
              }
            } catch (invoiceErr) {
              console.error('Error creating invoice:', invoiceErr);
              // Continue without invoice - booking still succeeded
            }
          }

          // Send confirmation emails
          try {
            await emailService.init();

            // Send confirmation to customer (invoice sent separately when admin confirms)
            const customerResult = await emailService.sendBookingConfirmation(bookingResult.booking, null);
            if (!customerResult.success) {
              console.warn('Customer confirmation email failed:', customerResult.error);
            }

            // Send notification to admin (Kirstie) - invoice visible in admin dashboard
            const adminResult = await emailService.sendBookingNotificationToAdmin(
              bookingResult.booking,
              null,
              bookingResult.isNewClient
            );
            if (!adminResult.success) {
              console.warn('Admin notification email failed:', adminResult.error);
            }
          } catch (emailErr) {
            console.error('Error sending confirmation emails:', emailErr);
            // Don't throw - booking still succeeded
          }
        } catch (bgErr) {
          console.error('Background tasks error:', bgErr);
        }
      }

      // Store confirmed booking details for display
      setConfirmedBooking({
        type: bookingType,
        service: selectedService,
        event: selectedEvent,
        date: selectedDate,
        time: requestCustomTime ? customTimeValue : selectedTime,
        isCustomTime: requestCustomTime,
        clientName: formData.name,
        clientEmail: formData.email
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting booking:', err);
      if (err.message === 'Event is full') {
        setError('Sorry, this event is now full. Please choose another event.');
        loadEvents(); // Refresh events
      } else {
        setError('There was an error submitting your booking. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookAnother = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
    setSelectedService(null);
    setSelectedEvent(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setRequestCustomTime(false);
    setCustomTimeValue('');
    setIsSubmitted(false);
    setConfirmedBooking(null);
    setBookingType('service');
    loadEvents(); // Refresh events to update spots
  };

  const goToPreviousWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const getShortName = (name) => {
    if (name.includes('Intuitive Reiki — Tune')) return 'Reiki Tune & Restore';
    if (name.includes('Intuitive Reiki — Intro')) return 'Reiki Intro & Integrate';
    if (name.includes('Deep Release')) return 'Deep Release & Rebalance';
    if (name.includes('Tarot')) return 'Tarot Reading';
    if (name.includes('Soul Alignment')) return 'Soul Alignment Coaching';
    if (name.includes('Chakra')) return 'Chakra Healing';
    if (name.includes('Space')) return 'Space Clearing';
    if (name.includes('Restorative')) return 'Restorative Reiki Class';
    if (name.includes('Guided Ascension')) return 'Guided Ascension Program';
    return name;
  };

  const formatPrice = (price) => {
    if (typeof price === 'number') {
      return `$${price}`;
    }
    return price; // Return string prices like "Price on enquiry" or "Varies"
  };

  const requiresConsultation = (service) => {
    // Services with non-numeric prices require consultation
    return typeof service.price !== 'number';
  };

  const getSpotsRemaining = (slot) => {
    const currentBookings = slotBookingCounts[slot] || 0;
    return Math.max(0, DEFAULT_SLOT_CAPACITY - currentBookings);
  };

  // Check if a time slot is too soon to book (for today's bookings)
  // Requires at least 60 minutes lead time before a slot can be booked
  const MIN_LEAD_TIME_MINUTES = 60;

  const isSlotInPast = (slot, date) => {
    if (!date || !isSameDay(date, new Date())) {
      return false; // Only check for today
    }
    const now = new Date();
    const [hours, minutes] = slot.split(':').map(Number);
    const slotTime = new Date();
    slotTime.setHours(hours, minutes, 0, 0);
    // Slot must be at least MIN_LEAD_TIME_MINUTES in the future
    const cutoff = new Date(now.getTime() + MIN_LEAD_TIME_MINUTES * 60 * 1000);
    return slotTime <= cutoff;
  };

  const formatEventDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const getEventStatusBadge = (event) => {
    if (event.spots?.available <= 0) {
      return <span className="event-status event-status--full">Full</span>;
    }
    if (event.spots?.available <= 3) {
      return <span className="event-status event-status--filling">Filling Fast</span>;
    }
    return <span className="event-status event-status--open">{event.spots?.available} spots left</span>;
  };

  return (
    <>
      <SEO
        title="Book a Reiki or Holistic Healing Session Sydney | KO Holistic Health"
        description="Book your Reiki healing, tarot reading, soul coaching or chakra healing session with Kirstie Warburton in Sydney. Choose your service, pick a date and time that suits you. Easy online booking."
        url="/booking"
        keywords="book reiki sydney, holistic healing appointment sydney, meditation consultation, spiritual coaching booking, book tarot reading sydney, chakra healing appointment, energy healing booking, book holistic therapist"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Book a Session', path: '/booking' }]}
      />

      <div className="booking-page">
        <section className="booking-hero">
          <div className="container">
            <h1>Book Your Session</h1>
            <p className="booking-hero__subtitle">
              Select your service and choose a time that works for you
            </p>
          </div>
        </section>

        <section className="section booking-content">
          <div className="container">
            {isSubmitted && confirmedBooking ? (
              <div className="booking-confirmation">
                <div className="confirmation-card">
                  <div className="confirmation-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>

                  <h2 className="confirmation-title">
                    {confirmedBooking.type === 'event' ? 'Registration Confirmed!' : 'Booking Request Received!'}
                  </h2>

                  <p className="confirmation-subtitle">
                    Thank you, {confirmedBooking.clientName}!
                    {confirmedBooking.type === 'event'
                      ? ' Your spot has been reserved.'
                      : ' Kirstie will review and confirm your booking shortly.'}
                  </p>

                  <div className="confirmation-details">
                    <div className="confirmation-details-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <span>Booking Details</span>
                    </div>

                    {confirmedBooking.type === 'event' ? (
                      <div className="confirmation-details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Event</span>
                          <span className="detail-value">{confirmedBooking.event?.title}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Date</span>
                          <span className="detail-value">{formatEventDate(confirmedBooking.event?.date)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Time</span>
                          <span className="detail-value">
                            {confirmedBooking.event?.time}
                            {confirmedBooking.event?.endTime && ` - ${confirmedBooking.event.endTime}`}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Location</span>
                          <span className="detail-value">{confirmedBooking.event?.location}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Price</span>
                          <span className="detail-value">${confirmedBooking.event?.price}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="confirmation-details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Service</span>
                          <span className="detail-value">{getShortName(confirmedBooking.service?.name)}</span>
                        </div>
                        {confirmedBooking.date && (
                          <div className="detail-item">
                            <span className="detail-label">Date</span>
                            <span className="detail-value">
                              {format(confirmedBooking.date, 'EEEE, MMMM d, yyyy')}
                            </span>
                          </div>
                        )}
                        {confirmedBooking.time && (
                          <div className="detail-item">
                            <span className="detail-label">Time</span>
                            <span className="detail-value">
                              {confirmedBooking.time}
                              {confirmedBooking.isCustomTime && ' (Custom Request)'}
                            </span>
                          </div>
                        )}
                        {!confirmedBooking.date && !confirmedBooking.time && (
                          <div className="detail-item">
                            <span className="detail-label">Scheduling</span>
                            <span className="detail-value">Kirstie will contact you to arrange a time</span>
                          </div>
                        )}
                        <div className="detail-item">
                          <span className="detail-label">Duration</span>
                          <span className="detail-value">{confirmedBooking.service?.duration}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Price</span>
                          <span className="detail-value">{formatPrice(confirmedBooking.service?.price)}</span>
                        </div>
                      </div>
                    )}

                    <div className="confirmation-status">
                      <span className="status-badge status-pending">Pending Confirmation</span>
                    </div>
                  </div>

                  <div className="confirmation-email-notice">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    <p>A confirmation email will be sent to <strong>{confirmedBooking.clientEmail}</strong> once your booking is confirmed.</p>
                  </div>

                  <div className="confirmation-actions">
                    <Button onClick={handleBookAnother} size="large">
                      Book Another Session
                    </Button>
                    <a href="/" className="confirmation-home-link">
                      Return to Home
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="booking-layout">
                {/* Booking Type Toggle */}
                {events.length > 0 && (
                  <div className="booking-type-toggle">
                    <button
                      className={`booking-type-btn ${bookingType === 'service' ? 'active' : ''}`}
                      onClick={() => {
                        setBookingType('service');
                        setSelectedEvent(null);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      Book a Service
                    </button>
                    <button
                      className={`booking-type-btn ${bookingType === 'event' ? 'active' : ''}`}
                      onClick={() => {
                        setBookingType('event');
                        setSelectedService(null);
                        setSelectedDate(null);
                        setSelectedTime(null);
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Register for Event
                    </button>
                  </div>
                )}

                {bookingType === 'event' ? (
                  <>
                    {/* Events Section */}
                    <div className="booking-step">
                      <h2>Step 1: Choose Your Event</h2>
                      <p className="step-subtitle">Select from our upcoming workshops, classes, and special events</p>

                      {loadingEvents ? (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>Loading events...</p>
                        </div>
                      ) : events.length === 0 ? (
                        <Card className="no-events">
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                            <line x1="16" y1="2" x2="16" y2="6"/>
                            <line x1="8" y1="2" x2="8" y2="6"/>
                            <line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          <h3>No Upcoming Events</h3>
                          <p>Check back soon for new workshops and classes, or book a private session instead.</p>
                          <Button onClick={() => setBookingType('service')}>Browse Services</Button>
                        </Card>
                      ) : (
                        <div className="events-grid">
                          {events.map((event) => (
                            <Card
                              key={event.id}
                              className={`event-card ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                              onClick={() => handleEventSelect(event)}
                            >
                              <div className="event-card__header">
                                <span className="event-card__category">{event.category}</span>
                                {getEventStatusBadge(event)}
                              </div>
                              <h3 className="event-card__title">{event.title}</h3>
                              <div className="event-card__details">
                                <div className="event-card__detail">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                    <line x1="16" y1="2" x2="16" y2="6"/>
                                    <line x1="8" y1="2" x2="8" y2="6"/>
                                    <line x1="3" y1="10" x2="21" y2="10"/>
                                  </svg>
                                  {formatEventDate(event.date)}
                                </div>
                                <div className="event-card__detail">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <polyline points="12 6 12 12 16 14"/>
                                  </svg>
                                  {event.time}{event.endTime && ` - ${event.endTime}`}
                                </div>
                                <div className="event-card__detail">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                    <circle cx="12" cy="10" r="3"/>
                                  </svg>
                                  {event.location}
                                </div>
                              </div>
                              {event.description && (
                                <p className="event-card__description">{event.description}</p>
                              )}
                              <div className="event-card__footer">
                                <span className="event-card__spots">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                  </svg>
                                  {event.spots?.available}/{event.spots?.total} spots
                                </span>
                                <span className="event-card__price">${event.price}</span>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Event Registration Form */}
                    {selectedEvent && (
                      <div className="booking-step" ref={eventDetailsRef}>
                        <h2>Step 2: Your Details</h2>
                        <Card className="booking-form-card">
                          <form onSubmit={handleSubmit} className="booking-form">
                            <div className="form-group">
                              <label htmlFor="name">Full Name *</label>
                              <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder="Your name"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="email">Email Address *</label>
                              <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                placeholder="your.email@example.com"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="phone">Phone Number *</label>
                              <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                placeholder="0400 000 000"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="message">Additional Notes (Optional)</label>
                              <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Any dietary requirements, accessibility needs, or questions..."
                              ></textarea>
                            </div>

                            {error && (
                              <div className="form-error">
                                {error}
                              </div>
                            )}

                            <div className="booking-summary-box">
                              <h3>Registration Summary</h3>
                              <p><strong>Event:</strong> {selectedEvent.title}</p>
                              <p><strong>Date:</strong> {formatEventDate(selectedEvent.date)}</p>
                              <p><strong>Time:</strong> {selectedEvent.time}{selectedEvent.endTime && ` - ${selectedEvent.endTime}`}</p>
                              <p><strong>Location:</strong> {selectedEvent.location}</p>
                              <p><strong>Price:</strong> ${selectedEvent.price}</p>
                              <p><strong>Spots Remaining:</strong> {selectedEvent.spots?.available}</p>
                            </div>

                            <Button type="submit" size="large" fullWidth disabled={isSubmitting}>
                              {isSubmitting ? 'Registering...' : 'Register for Event'}
                            </Button>
                          </form>
                        </Card>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Step 1: Select Service */}
                    <div className="booking-step service-selection-step">
                      <div className="step-header">
                        <span className="step-number">1</span>
                        <div>
                          <h2>Choose Your Service</h2>
                          <p className="step-subtitle">Select a service to get started with your booking</p>
                        </div>
                      </div>
                      {servicesLoading ? (
                        <div className="loading-state">
                          <div className="spinner"></div>
                          <p>Loading services...</p>
                        </div>
                      ) : (
                        <div className="service-grid">
                          {services.map((service) => {
                            const isSelected = selectedService?.id === service.id;
                            const isConsultation = requiresConsultation(service);
                            return (
                              <button
                                key={service.id}
                                type="button"
                                className={`service-card-btn ${isSelected ? 'selected' : ''} ${isConsultation ? 'consultation-required' : ''}`}
                                onClick={() => handleServiceSelect(service)}
                                aria-pressed={isSelected}
                              >
                                <div className="service-card__top">
                                  <h3 className="service-card__name">{getShortName(service.name)}</h3>
                                  {isSelected && (
                                    <span className="service-card__check">
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <polyline points="20 6 9 17 4 12"/>
                                      </svg>
                                    </span>
                                  )}
                                </div>
                                <p className="service-card__tagline">{service.tagline}</p>
                                <div className="service-card__meta">
                                  <span className="service-card__price">{formatPrice(service.price)}</span>
                                  <span className="service-card__divider"></span>
                                  <span className="service-card__duration">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <circle cx="12" cy="12" r="10"/>
                                      <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                    {service.duration}
                                  </span>
                                </div>
                                {(isConsultation || service.id === 'restorative-reiki') && (
                                  <div className="service-card__badges">
                                    {isConsultation && (
                                      <span className="service-card__badge service-card__badge--consult">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                        Consultation Required
                                      </span>
                                    )}
                                    {service.id === 'restorative-reiki' && (
                                      <span className="service-card__badge service-card__badge--group">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                          <circle cx="9" cy="7" r="4"/>
                                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                        </svg>
                                        Max 5 people
                                      </span>
                                    )}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Step 2: Select Date */}
                    {selectedService && (
                      <div className="booking-step" ref={dateStepRef}>
                        <h2>Step 2: Choose Your Date</h2>
                        {requiresConsultation(selectedService) ? (
                          <Card className="consultation-notice">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            <h3>This Service Requires a Consultation</h3>
                            <p>
                              <strong>{getShortName(selectedService.name)}</strong> pricing varies based on your specific needs.
                              Please fill in your details below and Kirstie will contact you to discuss your requirements and schedule your session.
                            </p>
                          </Card>
                        ) : (
                          <>
                            {/* Working Hours Info */}
                            {enabledDays && (
                              <div className="working-hours-info">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"/>
                                  <polyline points="12 6 12 12 16 14"/>
                                </svg>
                                <span>
                                  Available days: {Object.entries(enabledDays)
                                    .filter(([_, enabled]) => enabled)
                                    .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1, 3))
                                    .join(', ')
                                  }
                                </span>
                              </div>
                            )}

                            <div className="calendar-navigation">
                              <Button onClick={goToPreviousWeek} size="small">Previous Week</Button>
                              <Button onClick={goToToday} size="small" variant="secondary">Today</Button>
                              <Button onClick={goToNextWeek} size="small">Next Week</Button>
                            </div>
                            <div className="week-calendar">
                              {weekDates.map((date) => {
                                const isPast = isBefore(startOfDay(date), startOfDay(new Date()));
                                const isSelected = selectedDate && isSameDay(date, selectedDate);
                                const isToday = isSameDay(date, new Date());
                                const isBookable = isDateBookable(date);

                                return (
                                  <div
                                    key={date.toISOString()}
                                    className={`day-card ${isSelected ? 'selected' : ''} ${isPast ? 'disabled' : ''} ${isToday ? 'today' : ''} ${!isBookable ? 'closed' : ''}`}
                                    onClick={() => !isPast && isBookable && handleDateSelect(date)}
                                  >
                                    <div className="day-name">{format(date, 'EEE')}</div>
                                    <div className="day-number">{format(date, 'd')}</div>
                                    <div className="day-month">{format(date, 'MMM')}</div>
                                  </div>
                                );
                              })}
                            </div>

                            {weekDates.length === 0 && (
                              <div className="no-available-days">
                                <p>No available booking days this week. Please try the next week.</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Step 3: Select Time (only for services with numeric prices) */}
                    {selectedDate && !requiresConsultation(selectedService) && (
                      <div className="booking-step" ref={timeStepRef}>
                        <h2>Step 3: Choose Your Time</h2>
                        <p className="step-subtitle">Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>

                        {workingHours && (
                          <div className="working-hours-badge">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            Available: {workingHours.start} - {workingHours.end}
                          </div>
                        )}

                        {loadingAvailability ? (
                          <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading available times...</p>
                          </div>
                        ) : availableSlots.length === 0 ? (
                          <Card className="no-availability">
                            <p>No available times on this date. Please choose another date.</p>
                          </Card>
                        ) : (
                          <>
                            {scheduledEvents.length > 0 && (
                              <div className="event-conflict-notice">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                  <line x1="16" y1="2" x2="16" y2="6"/>
                                  <line x1="8" y1="2" x2="8" y2="6"/>
                                  <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                                <div>
                                  <strong>Event scheduled on this day:</strong>
                                  <span>{scheduledEvents.map(e => `${e.title} (${e.time}${e.endTime ? ` - ${e.endTime}` : ''})`).join(', ')}</span>
                                </div>
                              </div>
                            )}

                            {/* Buffer time notice */}
                            {bookingSettings && (bookingSettings.bufferTimeBefore > 0 || bookingSettings.bufferTimeAfter > 0) && (
                              <div className="buffer-time-notice">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"/>
                                  <polyline points="12 6 12 12 16 14"/>
                                </svg>
                                <span>
                                  {bookingSettings.bufferTimeBefore > 0 && bookingSettings.bufferTimeAfter > 0
                                    ? `A ${bookingSettings.bufferTimeBefore}-minute buffer before and ${bookingSettings.bufferTimeAfter}-minute buffer after each booking is required`
                                    : bookingSettings.bufferTimeBefore > 0
                                    ? `A ${bookingSettings.bufferTimeBefore}-minute buffer before each booking is required`
                                    : `A ${bookingSettings.bufferTimeAfter}-minute buffer after each booking is required`
                                  }
                                </span>
                              </div>
                            )}

                            <div className="time-slot-grid">
                              {timeSlots.map((slot) => {
                                const isPast = isSlotInPast(slot, selectedDate);
                                const isAvailable = availableSlots.includes(slot) && !isPast;
                                const isBlocked = blockedSlots.includes(slot);
                                const isBufferBlocked = bufferBlockedSlots.includes(slot);
                                const isEventBlocked = eventBlockedSlots.includes(slot);
                                const isBooked = bookedSlots.includes(slot);
                                const isSelected = selectedTime === slot;
                                const spotsRemaining = getSpotsRemaining(slot);

                                return (
                                  <button
                                    key={slot}
                                    className={`time-slot ${isSelected ? 'selected' : ''} ${!isAvailable ? 'unavailable' : ''} ${isEventBlocked ? 'event-blocked' : ''} ${isBufferBlocked ? 'buffer-blocked' : ''} ${isPast ? 'past' : ''}`}
                                    onClick={() => isAvailable && handleTimeSelect(slot)}
                                    disabled={!isAvailable}
                                  >
                                    <span className="time-slot__time">{slot}</span>
                                    {isAvailable && !isSelected && (
                                      <span className="time-slot__spots">{spotsRemaining} spot{spotsRemaining !== 1 ? 's' : ''}</span>
                                    )}
                                    {isPast && <span className="slot-status slot-status--past">Past</span>}
                                    {isEventBlocked && !isPast && <span className="slot-status slot-status--event">Event</span>}
                                    {isBufferBlocked && !isEventBlocked && !isBooked && !isBlocked && !isPast && <span className="slot-status slot-status--buffer">Buffer</span>}
                                    {isBlocked && !isEventBlocked && !isPast && <span className="slot-status">Blocked</span>}
                                    {isBooked && !isBlocked && !isEventBlocked && !isPast && <span className="slot-status">Booked</span>}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Custom Time Request Option */}
                            {bookingSettings?.allowCustomTimeRequests && (
                              <div className="custom-time-section">
                                <div className="custom-time-toggle">
                                  <label className="custom-time-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={requestCustomTime}
                                      onChange={(e) => {
                                        setRequestCustomTime(e.target.checked);
                                        if (!e.target.checked) {
                                          setCustomTimeValue('');
                                        }
                                        if (e.target.checked) {
                                          setSelectedTime(null);
                                        }
                                      }}
                                    />
                                    <span className="checkmark"></span>
                                    <span>Request a different time</span>
                                  </label>
                                  <span className="custom-time-hint">
                                    Can't find a suitable time? Request a custom time and we'll get back to you.
                                  </span>
                                </div>

                                {requestCustomTime && (
                                  <div className="custom-time-input">
                                    <label htmlFor="customTime">Preferred Time</label>
                                    <input
                                      type="time"
                                      id="customTime"
                                      value={customTimeValue}
                                      onChange={(e) => setCustomTimeValue(e.target.value)}
                                      min={workingHours?.start || '06:00'}
                                      max={workingHours?.end || '21:00'}
                                    />
                                    <span className="custom-time-note">
                                      Custom time requests require confirmation. You'll receive an email once approved.
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Step 4: Your Details */}
                    {(selectedTime || (requestCustomTime && customTimeValue) || (selectedService && requiresConsultation(selectedService))) && (
                      <div className="booking-step" ref={detailsStepRef}>
                        <h2>{requiresConsultation(selectedService) ? 'Step 3' : 'Step 4'}: Your Details</h2>
                        <Card className="booking-form-card">
                          <form onSubmit={handleSubmit} className="booking-form">
                            <div className="form-group">
                              <label htmlFor="name">Full Name *</label>
                              <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder="Your name"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="email">Email Address *</label>
                              <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                placeholder="your.email@example.com"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="phone">Phone Number *</label>
                              <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                placeholder="0400 000 000"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="message">
                                {requiresConsultation(selectedService)
                                  ? 'Tell us about your needs *'
                                  : 'Additional Notes (Optional)'}
                              </label>
                              <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows="4"
                                required={requiresConsultation(selectedService)}
                                placeholder={requiresConsultation(selectedService)
                                  ? 'Please describe what you\'re looking for, any specific requirements, and your preferred availability...'
                                  : 'Any specific concerns or requirements...'}
                              ></textarea>
                            </div>

                            {error && (
                              <div className="form-error">
                                {error}
                              </div>
                            )}

                            <div className="booking-summary-box">
                              <h3>Booking Summary</h3>
                              <p><strong>Service:</strong> {getShortName(selectedService.name)}</p>
                              {!requiresConsultation(selectedService) && (
                                <>
                                  <p><strong>Date:</strong> {format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                                  <p><strong>Time:</strong> {requestCustomTime ? `${customTimeValue} (Custom Request)` : selectedTime}</p>
                                  {requestCustomTime && (
                                    <p className="custom-time-warning">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="12" y1="8" x2="12" y2="12"/>
                                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                                      </svg>
                                      Custom time request - subject to availability
                                    </p>
                                  )}
                                </>
                              )}
                              <p><strong>Duration:</strong> {selectedService.duration}</p>
                              <p><strong>Price:</strong> {formatPrice(selectedService.price)}</p>
                            </div>

                            <Button type="submit" size="large" fullWidth disabled={isSubmitting}>
                              {isSubmitting
                                ? 'Submitting...'
                                : requiresConsultation(selectedService)
                                  ? 'Request Consultation'
                                  : requestCustomTime
                                    ? 'Submit Custom Time Request'
                                    : 'Confirm Booking'}
                            </Button>
                          </form>
                        </Card>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Booking;
